import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { SAVE_SLOTS, listArchive, slotSummaries, type ArchivedRun, type LossMode, type SaveDoc } from '@bratva/state';
import { isMuted, toggleMute } from '../game/sound.js';
import { store } from '../game/store.js';

interface Props {
  save: SaveDoc;
  onClose(): void;
}

const rub = (kop: number): string => `${(kop / 100).toLocaleString('ru-RU')} ₽`;

const MODES: { mode: LossMode; title: string; note: string }[] = [
  {
    mode: 'mamkin',
    title: 'По-мамкиному',
    note: 'Суд отскакивает адвокатом, здоровье в ноль — больничка. Прохождение не теряется.',
  },
  {
    mode: 'pacanski',
    title: 'По-пацански',
    note: 'Срок и могила закрывают дело навсегда. Под это и балансируется риск.',
  },
];

const OUTCOMES: Record<ArchivedRun['outcome'], string> = {
  srok: 'Срок',
  mogila: 'Могила',
  rayon: 'Взял район',
};

/** Дела: три слота, архив закрытых прохождений и выбор режима для нового. */
export function RunPanel({ save, onClose }: Props): JSX.Element {
  const [slots, setSlots] = useState<Record<number, SaveDoc | null>>({});
  const [archive, setArchive] = useState<ArchivedRun[]>([]);
  const [muted, setMuted] = useState(isMuted());

  useEffect(() => {
    void slotSummaries().then(setSlots);
    void listArchive().then(setArchive);
  }, [save.day, save.outcome]);

  return (
    <div className="shop">
      <div className="deeds-head">
        <b>Дела</b>
        <button type="button" className="deeds-close" onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </div>

      <div className="shop-list">
        {SAVE_SLOTS.map((slot) => {
          const doc = slots[slot];
          const current = store.currentSlot === slot;
          return (
            <div key={slot} className={current ? 'shop-item worn' : 'shop-item'}>
              <div className="shop-item-top">
                <span className="shop-item-title">Дело №{slot}</span>
                <span className="shop-item-price">{current ? 'открыто' : ''}</span>
              </div>
              <span className="shop-item-meta">
                {doc
                  ? `день ${doc.day} · ур. ${doc.player.level} · ${rub(doc.player.cash)} · ${
                      doc.mode === 'pacanski' ? 'по-пацански' : 'по-мамкиному'
                    }${doc.outcome === 'active' ? '' : ` · ${OUTCOMES[doc.outcome]}`}`
                  : 'пусто'}
              </span>
              {!current && (
                <div className="shop-item-actions">
                  <button type="button" onClick={() => void store.useSlot(slot)}>
                    {doc ? 'Открыть' : 'Начать здесь'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="shop-sets">
        <span className="hud-caption">Новое дело в этом слоте</span>
        {MODES.map((option) => (
          <button
            key={option.mode}
            type="button"
            className="deed"
            onClick={() => void store.startNew(option.mode)}
          >
            <span className="deed-title">{option.title}</span>
            <span className="deed-note">{option.note}</span>
          </button>
        ))}
      </div>

      <div className="shop-sets">
        <span className="hud-caption">Звук</span>
        <button
          type="button"
          onClick={() => {
            toggleMute();
            setMuted(isMuted());
          }}
        >
          {muted ? 'Включить' : 'Выключить'}
        </button>
        <span className="shop-item-meta">Синтезируется на месте, файлов нет.</span>
      </div>

      {archive.length > 0 && (
        <div className="shop-sets">
          <span className="hud-caption">Архив</span>
          {archive.slice(0, 8).map((run) => (
            <p key={run.finishedAt} className="set">
              <b>{OUTCOMES[run.outcome]}</b>{' '}
              <span>
                день {run.day} · ур. {run.level} · {rub(run.cash)}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
